import { getFunctions, httpsCallable } from 'firebase/functions';

interface ConfirmationEmailData {
  email: string;
  name: string;
  loginUrl: string;
  hasPassword: boolean;
  password?: string; // Apenas para exibição no email, se disponível
}

class ConfirmationEmailService {
  private static instance: ConfirmationEmailService;

  private constructor() {
    console.log('📧 [CONFIRMATION-EMAIL] Serviço de email inicializado');
  }

  public static getInstance(): ConfirmationEmailService {
    if (!ConfirmationEmailService.instance) {
      ConfirmationEmailService.instance = new ConfirmationEmailService();
    }
    return ConfirmationEmailService.instance;
  }

  /**
   * Envia email de confirmação de cadastro ao usuário
   */
  async sendConfirmationEmail(data: ConfirmationEmailData): Promise<void> {
    try {
      console.log(`📧 [CONFIRMATION-EMAIL] Enviando email de confirmação para: ${data.email}`);
      
      const functions = getFunctions();
      const sendWelcomeEmail = httpsCallable(functions, 'sendWelcomeEmail');
      
      await sendWelcomeEmail({
        email: data.email,
        name: data.name,
        loginUrl: data.loginUrl,
        hasPassword: data.hasPassword,
        password: data.password,
      });

      console.log(`✅ [CONFIRMATION-EMAIL] Email de boas-vindas enviado com sucesso`);
    } catch (error: any) {
      // Tratamento específico de erros
      const errorCode = error?.code || '';
      const errorMessage = error?.message || '';
      
      // Erros de Cloud Function não disponível
      if (errorCode === 'functions/unavailable' || errorCode === 'functions/not-found') {
        console.warn('⚠️ [CONFIRMATION-EMAIL] Cloud Function não disponível. Verifique se está deployada.');
        return;
      }
      
      // Erros de SMTP/DNS (do backend)
      if (errorMessage.includes('EBADNAME') || 
          errorMessage.includes('ENOTFOUND') || 
          errorMessage.includes('SMTP') ||
          errorMessage.includes('transportador SMTP')) {
        console.error('❌ [CONFIRMATION-EMAIL] Erro de configuração SMTP:', {
          code: errorCode,
          message: errorMessage,
          details: 'Verifique as configurações SMTP no backend (SMTP_HOST, SMTP_USER, SMTP_PASSWORD)'
        });
        // Não bloquear o fluxo - email não é crítico para onboarding
        return;
      }
      
      // Erros genéricos
      console.error('❌ [CONFIRMATION-EMAIL] Erro ao enviar email de confirmação:', {
        code: errorCode,
        message: errorMessage,
        error: error
      });
      
      // Não lançar erro para não interromper o fluxo de onboarding
      // O email não é crítico - o usuário pode acessar o sistema mesmo sem receber o email
    }
  }

  // Método removido - templates agora estão na Cloud Function
  // Mantido apenas para referência/compatibilidade se necessário
  private buildConfirmationTemplate(data: ConfirmationEmailData): { subject: string; html: string; text: string } {
    const subject = `🎉 Bem-vindo ao UpClinic! Sua conta foi criada com sucesso`;
    
    // Dados de acesso
    let accessInfo = '';
    if (data.hasPassword && data.password) {
      accessInfo = `
        <div style="background: #e3f2fd; border-left: 4px solid #1976d2; padding: 15px; margin: 20px 0; border-radius: 4px;">
          <h3 style="margin-top: 0; color: #1976d2;">🔑 Suas Credenciais de Acesso:</h3>
          <p style="margin: 5px 0;"><strong>Email:</strong> ${data.email}</p>
          <p style="margin: 5px 0;"><strong>Senha:</strong> ${data.password}</p>
          <p style="margin-top: 10px; font-size: 13px; color: #666;">
            <strong>⚠️ Importante:</strong> Guarde estas informações em local seguro. 
            Você pode alterar sua senha a qualquer momento no sistema.
          </p>
        </div>
      `;
    } else {
      accessInfo = `
        <div style="background: #e8f5e9; border-left: 4px solid #4caf50; padding: 15px; margin: 20px 0; border-radius: 4px;">
          <h3 style="margin-top: 0; color: #4caf50;">🔐 Acesso via Google</h3>
          <p style="margin: 5px 0;">Você criou sua conta usando sua conta Google.</p>
          <p style="margin: 5px 0;">Para acessar o sistema, use o botão "Continuar com Google" na página de login.</p>
        </div>
      `;
    }
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Bem-vindo ao UpClinic</title>
        <style>
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
            line-height: 1.6; 
            color: #333; 
            margin: 0; 
            padding: 0; 
            background-color: #f5f5f5;
          }
          .container { 
            max-width: 600px; 
            margin: 0 auto; 
            background: #ffffff;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .header { 
            background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%); 
            color: white; 
            padding: 40px 20px; 
            text-align: center; 
          }
          .header h1 {
            margin: 0;
            font-size: 28px;
          }
          .content { 
            padding: 30px 20px; 
          }
          .button { 
            background: #1976d2; 
            color: white; 
            padding: 15px 30px; 
            text-decoration: none; 
            border-radius: 8px; 
            display: inline-block; 
            margin: 20px 0; 
            font-weight: bold;
            text-align: center;
          }
          .button:hover { 
            background: #1565c0; 
          }
          .footer { 
            background: #f8f9fa; 
            padding: 20px; 
            text-align: center; 
            font-size: 12px; 
            color: #666;
            border-top: 1px solid #e0e0e0;
          }
          .features {
            background: #f9f9f9;
            padding: 20px;
            border-radius: 8px;
            margin: 20px 0;
          }
          .feature-item {
            padding: 8px 0;
            display: flex;
            align-items: flex-start;
          }
          .feature-icon {
            margin-right: 10px;
            font-size: 18px;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>🏥 Bem-vindo ao UpClinic!</h1>
            <p style="margin: 10px 0 0 0; opacity: 0.9;">Sua conta foi criada com sucesso</p>
          </div>
          
          <div class="content">
            <h2>Olá, ${data.name}! 👋</h2>
            
            <p>É um prazer ter você conosco! Sua conta no <strong>UpClinic</strong> foi criada e está pronta para uso.</p>
            
            ${accessInfo}
            
            <div style="text-align: center; margin: 30px 0;">
              <a href="${data.loginUrl}" class="button">🚀 Acessar o Sistema</a>
            </div>
            
            <div class="features">
              <h3 style="margin-top: 0; color: #1976d2;">✨ O que você pode fazer:</h3>
              
              <div class="feature-item">
                <span class="feature-icon">📋</span>
                <span><strong>Gestão de Pacientes:</strong> Cadastre e gerencie todos os seus pacientes de forma organizada</span>
              </div>
              
              <div class="feature-item">
                <span class="feature-icon">📅</span>
                <span><strong>Agendamentos:</strong> Gerencie sua agenda e consultas com facilidade</span>
              </div>
              
              <div class="feature-item">
                <span class="feature-icon">💊</span>
                <span><strong>Farmácia:</strong> Controle completo de medicamentos e prescrições</span>
              </div>
              
              <div class="feature-item">
                <span class="feature-icon">🧪</span>
                <span><strong>Laboratório:</strong> Solicite exames e acompanhe resultados</span>
              </div>
              
              <div class="feature-item">
                <span class="feature-icon">💰</span>
                <span><strong>Financeiro:</strong> Gerencie receitas, despesas e relatórios financeiros</span>
              </div>
              
              <div class="feature-item">
                <span class="feature-icon">📊</span>
                <span><strong>Relatórios:</strong> Acesse insights e análises do seu negócio</span>
              </div>
            </div>
            
            <div style="background: #fff3cd; border: 1px solid #ffc107; padding: 15px; border-radius: 8px; margin: 20px 0;">
              <strong>💡 Dica:</strong> Complete o onboarding para configurar seu perfil e começar a usar todas as funcionalidades do sistema.
            </div>
            
            <p><strong>📞 Precisa de ajuda?</strong></p>
            <p>Nossa equipe está pronta para ajudá-lo:</p>
            <ul>
              <li>📧 Email: suporte@upclinic.com.br</li>
              <li>📱 WhatsApp: (62) 99672-0296</li>
              <li>🌐 Suporte 24/7 no sistema</li>
            </ul>
          </div>
          
          <div class="footer">
            <p><strong>UpClinic - Sistema de Gestão Médica</strong></p>
            <p>© 2024 UpClinic. Todos os direitos reservados.</p>
            <p style="margin-top: 10px; font-size: 11px; color: #999;">
              Este é um email automático do sistema UpClinic.<br>
              Se você não criou esta conta, entre em contato conosco imediatamente.
            </p>
          </div>
        </div>
      </body>
      </html>
    `;
    
    const text = `
      Bem-vindo ao UpClinic!
      
      Olá, ${data.name}!
      
      É um prazer ter você conosco! Sua conta no UpClinic foi criada e está pronta para uso.
      
      ${data.hasPassword && data.password ? `
      🔑 SUAS CREDENCIAIS DE ACESSO:
      
      Email: ${data.email}
      Senha: ${data.password}
      
      ⚠️ Importante: Guarde estas informações em local seguro. 
      Você pode alterar sua senha a qualquer momento no sistema.
      ` : `
      🔐 ACESSO VIA GOOGLE
      
      Você criou sua conta usando sua conta Google.
      Para acessar o sistema, use o botão "Continuar com Google" na página de login.
      `}
      
      🚀 ACESSAR O SISTEMA:
      ${data.loginUrl}
      
      ✨ O QUE VOCÊ PODE FAZER:
      
      📋 Gestão de Pacientes: Cadastre e gerencie todos os seus pacientes de forma organizada
      📅 Agendamentos: Gerencie sua agenda e consultas com facilidade
      💊 Farmácia: Controle completo de medicamentos e prescrições
      🧪 Laboratório: Solicite exames e acompanhe resultados
      💰 Financeiro: Gerencie receitas, despesas e relatórios financeiros
      📊 Relatórios: Acesse insights e análises do seu negócio
      
      💡 Dica: Complete o onboarding para configurar seu perfil e começar a usar todas as funcionalidades do sistema.
      
      📞 PRECISA DE AJUDA?
      
      Nossa equipe está pronta para ajudá-lo:
      📧 Email: suporte@upclinic.com.br
      📱 WhatsApp: (62) 99672-0296
      🌐 Suporte 24/7 no sistema
      
      ---
      
      UpClinic - Sistema de Gestão Médica
      © 2024 UpClinic. Todos os direitos reservados.
      
      Este é um email automático do sistema UpClinic.
      Se você não criou esta conta, entre em contato conosco imediatamente.
    `;
    
    return { subject, html, text };
  }
}

export const confirmationEmailService = ConfirmationEmailService.getInstance();

